import * as React from "react";
import {
  Box,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import LocalPrintshopRoundedIcon from "@mui/icons-material/LocalPrintshopRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";

type Props = {
  printerStatus?: boolean;
  connected?: boolean;
  isConnecting?: boolean;
  modelName?: string;
  queueCount?: number;
  lastError?: string;
  lastSeenAt?: Date | string;
  compact?: boolean;
  onTestPrint?: () => void;
  onRetry?: () => void;
  onOpenSettings?: () => void;
};

export function PrinterStatusHeader({
  printerStatus,
  connected,
  isConnecting = false,
  modelName,
  queueCount,
  lastError,
  lastSeenAt,
  compact = true,
  onTestPrint,
  onRetry,
  onOpenSettings,
}: Props) {
  const theme = useTheme();
  const isConnected = connected ?? printerStatus ?? false;

  const status = isConnecting
    ? { key: "connecting", label: "Syncing", color: theme.palette.warning.main, Icon: AutorenewRoundedIcon }
    : isConnected
    ? { key: "connected", label: "Ready", color: theme.palette.success.main, Icon: CheckCircleRoundedIcon }
    : { key: "disconnected", label: "Offline", color: theme.palette.error.main, Icon: CancelRoundedIcon };

  const tooltip = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: alpha("#fff", 0.6), letterSpacing: 1, display: 'block', mb: 1 }}>
        PRINTER STATUS
      </Typography>
      <Stack spacing={0.5}>
        <TooltipRow label="State" value={status.label} color={status.color} />
        {modelName && <TooltipRow label="Model" value={modelName} />}
        {typeof queueCount === "number" && <TooltipRow label="Queue" value={`${queueCount} jobs`} />}
        {lastSeenAt && <TooltipRow label="Last Seen" value={formatTime(lastSeenAt)} />}
        {lastError && (
          <Typography color="error.light" variant="caption" sx={{ mt: 1, borderTop: '1px solid rgba(255,255,255,0.1)', pt: 0.5 }}>
            {lastError}
          </Typography>
        )}
      </Stack>
    </Box>
  );

  // ---------- COMPACT MODE ----------
  if (compact) {
    return (
      <Tooltip title={tooltip} arrow placement="bottom">
        <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <LocalPrintshopRoundedIcon
            sx={{ 
                color: status.color, 
                fontSize: 22,
                filter: `drop-shadow(0 0 4px ${alpha(status.color, 0.4)})`
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  // ---------- FULL MODE (Premium Pill Design) ----------
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        bgcolor: alpha(theme.palette.text.primary, 0.04),
        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        borderRadius: "12px",
        pl: 1.5,
        pr: 0.5,
        py: 0.5,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          bgcolor: alpha(theme.palette.text.primary, 0.06),
          borderColor: alpha(status.color, 0.3),
        }
      }}
    >
      {/* Status Group */}
      <Tooltip title={tooltip} arrow>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ cursor: 'default' }}>
          <Box sx={{ position: "relative", display: "flex" }}>
            <LocalPrintshopRoundedIcon sx={{ color: alpha(theme.palette.text.primary, 0.7), fontSize: 20 }} />
            <Box
              sx={{
                position: "absolute",
                right: -4,
                top: -4,
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: status.color,
                border: `2px solid ${theme.palette.background.paper}`,
                boxShadow: `0 0 8px ${alpha(status.color, 0.6)}`,
                ...(status.key === "connecting" && {
                  animation: "pulse 1.5s infinite ease-in-out",
                }),
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)", opacity: 1 },
                  "50%": { transform: "scale(1.4)", opacity: 0.5 },
                  "100%": { transform: "scale(1)", opacity: 1 },
                }
              }}
            />
          </Box>

          <Stack spacing={-0.2}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              {status.label}
            </Typography>
            {modelName && (
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: '0.65rem', fontWeight: 600 }}>
                {modelName} {typeof queueCount === 'number' && `(${queueCount})`}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Tooltip>

      {/* Action Group */}
      {(onTestPrint || onRetry || onOpenSettings) && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1.5, my: 0.5, opacity: 0.5 }} />
          <Stack direction="row" spacing={0.5}>
            {onTestPrint && (
              <ActionButton title="Test Print" onClick={onTestPrint} disabled={!isConnected} icon={<PrintRoundedIcon />} />
            )}
            {onRetry && (
              <ActionButton 
                title={status.key === "connected" ? "Reconnect" : "Retry"} 
                onClick={onRetry} 
                disabled={status.key === "connecting"} 
                icon={<ReplayRoundedIcon />} 
                isSpinning={status.key === "connecting"}
              />
            )}
            {onOpenSettings && (
              <ActionButton title="Settings" onClick={onOpenSettings} icon={<SettingsRoundedIcon />} />
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}

// Sub-components for cleaner code
const TooltipRow = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <Stack direction="row" justifyContent="space-between" spacing={3}>
    <Typography variant="caption" sx={{ color: alpha("#fff", 0.5) }}>{label}</Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, color: color || "#fff" }}>{value}</Typography>
  </Stack>
);

const ActionButton = ({ title, onClick, disabled, icon, isSpinning }: any) => (
  <Tooltip title={title} arrow>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          p: 0.5,
          color: "text.secondary",
          "&:hover": { color: "primary.main", bgcolor: alpha("#000", 0.05) },
          ...(isSpinning && {
            animation: "spin 2s linear infinite",
          }),
          "@keyframes spin": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </IconButton>
    </span>
  </Tooltip>
);

function formatTime(v: Date | string) {
  const d = typeof v === "string" ? new Date(v) : v;
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}